import PageMeta from "@/components/PageMeta";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { fetchProducts, fetchCollection } from "@/lib/api";
import { MURAL_COLLECTIONS, classifyMuralAll } from "@/lib/muralCollections";
import { buildMuralQuoteUrl } from "@/lib/quotePrefill";
import { getRouteState } from "@/lib/navigation";
import ProductCard from "@/components/ProductCard";
import SectionFadeIn from "@/components/SectionFadeIn";

function isMuralProduct(product) {
  const pt = (product.productType || product.product_type || "").toLowerCase();
  return pt.includes("mural") || pt.includes("wall mural");
}

export default function MuralCollectionPage() {
  const { collectionSlug } = useParams();
  const location = useLocation();
  const [allMurals, setAllMurals] = useState([]);
  const [loading, setLoading] = useState(true);

  const collection = useMemo(
    () => MURAL_COLLECTIONS.find((c) => c.slug === collectionSlug),
    [collectionSlug]
  );

  useEffect(() => {
    let active = true;
    setLoading(true);

    async function load() {
      try {
        const colData = await fetchCollection("wall-murals");
        const products = (colData.collection?.products || []).filter(isMuralProduct);
        if (products.length > 0) {
          if (active) setAllMurals(products);
          return;
        }
      } catch (err) {
        console.error("MuralCollectionPage: collection fetch failed", err);
      }

      try {
        const data = await fetchProducts({ first: 250 });
        const products = (data.products || []).filter(isMuralProduct);
        if (active) setAllMurals(products);
      } catch (err) {
        console.error("[MuralCollectionPage] fetch failed:", err);
        if (active) setAllMurals([]);
      }
    }

    load().finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // All referenced names are module-level imports, stable useState setters,
    // or locals scoped inside this effect. No real dependencies — run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter products matching this collection
  const matchedProducts = useMemo(() => {
    if (!collection) return [];
    return allMurals.filter((p) => {
      const slugs = classifyMuralAll(p);
      return slugs.includes(collection.slug);
    });
  }, [allMurals, collection]);

  // Products with images first
  const sorted = useMemo(() => {
    return [...matchedProducts].sort((a, b) => {
      const aImg = a.images?.length > 0 ? 0 : 1;
      const bImg = b.images?.length > 0 ? 0 : 1;
      return aImg - bImg;
    });
  }, [matchedProducts]);

  // Other collections for "Explore more"
  const otherCollections = MURAL_COLLECTIONS.filter(
    (c) => c.slug !== collectionSlug
  ).slice(0, 4);

  const quoteHref = buildMuralQuoteUrl({
    dimensionsText: "",
    materialLabel: "",
    estimatedPrice: 0,
    style: collection?.title,
  });

  if (!collection) {
    return (
      <div className="py-24 text-center max-w-7xl mx-auto px-6">
        <h1 className="text-2xl text-brand-text">Collection not found</h1>
        <Link to="/wall-murals-landing" className="mt-4 inline-block text-brand-gold hover:underline">
          Back to Wall Murals
        </Link>
      </div>
    );
  }

  const Icon = collection.icon;

  return (
    <div className="py-16 md:py-24" data-testid="mural-collection-page">
      <PageMeta
        title={`${collection.title} Wall Murals`}
        description={collection.description}
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* Back link */}
        <Link
          to="/wall-murals-landing"
          state={getRouteState(location)}
          className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.12em] text-brand-text-secondary/60 hover:text-brand-gold transition-colors duration-300 mb-8"
          data-testid="back-to-murals"
        >
          <ArrowLeft size={14} /> Wall Murals
        </Link>

        {/* Hero for this collection */}
        <SectionFadeIn>
          <div
            className="relative rounded-sm overflow-hidden border border-white/[0.06]"
            data-testid="collection-hero"
          >
            <div className="aspect-[4/3] md:aspect-[21/7]">
              <img
                src={collection.image}
                alt={collection.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0A0D14]/90 via-[#0A0D14]/60 to-[#0A0D14]/20" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14]/70 to-transparent" />
            </div>
            <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-10">
              <div className="flex items-center gap-2.5 text-brand-gold mb-3">
                <Icon size={20} />
                <span className="text-[11px] uppercase tracking-[0.2em] font-semibold">
                  Mural Collection
                </span>
              </div>
              <h1
                className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-brand-text max-w-xl"
                data-testid="collection-title"
              >
                {collection.title}
              </h1>
              <p className="mt-3 text-[14px] text-brand-text-secondary/80 max-w-lg leading-relaxed">
                {collection.description}
              </p>
            </div>
          </div>
        </SectionFadeIn>

        {/* Products grid */}
        <section className="mt-12" data-testid="collection-products">
          {loading ? (
            <div className="py-16 text-center">
              <div className="inline-block w-6 h-6 border-2 border-brand-gold/30 border-t-brand-gold rounded-full animate-spin" />
              <p className="mt-3 text-[13px] text-brand-text-secondary/40">Loading designs...</p>
            </div>
          ) : sorted.length > 0 ? (
            <>
              <div className="flex items-end justify-between mb-6">
                <p className="text-[13px] text-brand-text-secondary/60">
                  {sorted.length} design{sorted.length !== 1 && "s"} in this collection
                </p>
                <Link
                  to="/collections/wall-murals"
                  state={getRouteState(location)}
                  className="text-[12px] uppercase tracking-[0.1em] text-brand-text-secondary/50 hover:text-brand-gold transition-colors"
                >
                  View all murals
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {sorted.map((product, i) => (
                  <SectionFadeIn key={product.id} delay={i * 60}>
                    <ProductCard
                      product={product}
                      variant="mural"
                      basePath="/collections/wall-murals"
                    />
                  </SectionFadeIn>
                ))}
              </div>
            </>
          ) : (
            <div
              className="rounded-sm border border-white/[0.06] bg-white/[0.02] py-16 px-6 text-center"
              data-testid="collection-empty"
            >
              <Icon size={36} strokeWidth={1} className="mx-auto text-brand-gold/30 mb-4" />
              <h3 className="text-[18px] font-medium text-brand-text mb-2">
                {collection.title} murals available to order
              </h3>
              <p className="text-[13px] text-brand-text-secondary/60 max-w-md mx-auto leading-relaxed">
                We print this style made-to-measure. Send us a reference image or describe what you're looking for and we'll quote it.
              </p>
              <Link
                to="/request-quote"
                state={getRouteState(location)}
                className="inline-flex items-center gap-2 mt-6 bg-brand-gold text-brand-bg hover:bg-brand-gold-hover transition-colors duration-300 rounded-sm px-7 py-3.5 font-medium text-[13px] tracking-wide"
                data-testid="collection-empty-quote-cta"
              >
                Request a {collection.title} Quote <ArrowRight size={15} />
              </Link>
            </div>
          )}
        </section>

        {/* Quote CTA */}
        <SectionFadeIn>
          <section
            className="mt-14 rounded-sm border border-brand-gold/15 bg-[linear-gradient(135deg,rgba(203,160,82,0.04),rgba(10,13,20,0.97))] px-6 py-10 md:px-10"
            data-testid="collection-quote-cta"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
              <div>
                <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-brand-gold mb-1">
                  Made to measure
                </p>
                <h3 className="text-[20px] font-heading tracking-tight text-brand-text">
                  Have a {collection.title.toLowerCase()} design in mind?
                </h3>
                <p className="mt-1 text-[13px] text-brand-text-secondary/60">
                  Send us your wall dimensions and a reference image. Same pricing: &pound;14&ndash;&pound;22/m&sup2;.
                </p>
              </div>
              <Link
                to={quoteHref}
                state={getRouteState(location)}
                className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-bg hover:bg-brand-gold-hover transition-colors duration-300 rounded-sm px-8 py-3.5 font-semibold text-[13px] tracking-wide shrink-0"
                data-testid="collection-request-quote"
              >
                Request a Quote <ArrowRight size={15} />
              </Link>
            </div>
          </section>
        </SectionFadeIn>

        {/* Explore more collections */}
        <SectionFadeIn>
          <section className="mt-16" data-testid="explore-more-collections">
            <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-brand-gold mb-6">
              Explore more styles
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {otherCollections.map((col) => {
                const ColIcon = col.icon;
                return (
                  <Link
                    key={col.slug}
                    to={`/wall-murals/${col.slug}`}
                    state={getRouteState(location)}
                    className="group rounded-sm border border-white/[0.06] bg-white/[0.02] p-4 hover:border-brand-gold/30 transition-all duration-300"
                    data-testid={`explore-${col.slug}`}
                  >
                    <ColIcon
                      size={18}
                      className="text-brand-gold/60 group-hover:text-brand-gold transition-colors duration-300"
                    />
                    <p className="mt-2 text-[13px] font-medium text-brand-text group-hover:text-brand-gold transition-colors duration-300">
                      {col.title}
                    </p>
                    <p className="mt-0.5 text-[11px] text-brand-text-secondary/40 line-clamp-1">
                      {col.short}
                    </p>
                  </Link>
                );
              })}
            </div>
          </section>
        </SectionFadeIn>
      </div>
    </div>
  );
}
